/**
 * ProjectManagementTourContext.jsx
 * Central state and persistence provider for the Project Management Product Tour.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { TOUR_DEFINITIONS } from './tourSteps';

const ProjectManagementTourContext = createContext(null);

function ProjectManagementTourProviderInternal({ children }) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const userId = currentAdmin?._id || 'guest';
    const storageKey = useMemo(() => `pm_tour_state_${userId}`, [userId]);

    // Initial state from localStorage
    const [storageState, setStorageState] = useState(() => {
        try {
            const raw = localStorage.getItem(`pm_tour_state_${userId}`);
            if (raw) return JSON.parse(raw);
        } catch (e) {
            // fallback
        }
        return {
            inviteDismissed: false,
            completed: {},
        };
    });

    // Re-sync if user ID changes
    useEffect(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                setStorageState(JSON.parse(raw));
            } else {
                setStorageState({ inviteDismissed: false, completed: {} });
            }
        } catch (e) {
            // silent
        }
    }, [storageKey]);

    const persistState = useCallback((updater) => {
        setStorageState((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            try {
                localStorage.setItem(storageKey, JSON.stringify(next));
            } catch (e) {
                console.warn('[Tour] Failed to persist tour state to localStorage:', e);
            }
            return next;
        });
    }, [storageKey]);

    // Active tour state
    const [isOpen, setIsOpen] = useState(false);
    const [activeTourId, setActiveTourId] = useState('taskBoard');
    const [currentStep, setCurrentStep] = useState(0);

    // Callbacks registered by views (e.g. to switch tabs or open drawers during tour)
    const stepChangeListenersRef = useRef(new Set());

    const registerStepChangeListener = useCallback((listener) => {
        stepChangeListenersRef.current.add(listener);
        return () => {
            stepChangeListenersRef.current.delete(listener);
        };
    }, []);

    const startTour = useCallback((tourId = 'taskBoard', initialStep = 0) => {
        const def = TOUR_DEFINITIONS[tourId] || TOUR_DEFINITIONS.taskBoard;
        setActiveTourId(def.id);
        setCurrentStep(Math.max(0, Math.min(initialStep, def.steps.length - 1)));
        setIsOpen(true);

        // Notify listeners of initial step
        stepChangeListenersRef.current.forEach((fn) => {
            try {
                fn({ tourId: def.id, stepIndex: initialStep, step: def.steps[initialStep] });
            } catch (e) {
                console.error('[Tour] Listener error on start:', e);
            }
        });
    }, []);

    const closeTour = useCallback(() => {
        setIsOpen(false);
    }, []);

    const completeActiveTour = useCallback(() => {
        setIsOpen(false);
        if (activeTourId) {
            persistState((prev) => ({
                ...prev,
                inviteDismissed: true,
                completed: {
                    ...prev.completed,
                    [activeTourId]: true,
                },
            }));
        }
    }, [activeTourId, persistState]);

    const handleStepChange = useCallback((newStep) => {
        const def = TOUR_DEFINITIONS[activeTourId];
        if (!def) return;
        setCurrentStep(newStep);

        const targetStep = def.steps[newStep];
        stepChangeListenersRef.current.forEach((fn) => {
            try {
                fn({ tourId: activeTourId, stepIndex: newStep, step: targetStep });
            } catch (e) {
                console.error('[Tour] Listener error on step change:', e);
            }
        });
    }, [activeTourId]);

    const dismissInvite = useCallback(() => {
        persistState((prev) => ({
            ...prev,
            inviteDismissed: true,
        }));
    }, [persistState]);

    const resetProgress = useCallback((tourId = null) => {
        persistState((prev) => {
            if (tourId) {
                const nextCompleted = { ...prev.completed };
                delete nextCompleted[tourId];
                return { ...prev, completed: nextCompleted };
            }
            return { inviteDismissed: false, completed: {} };
        });
    }, [persistState]);

    const isCompleted = useCallback((tourId) => {
        return Boolean(storageState.completed?.[tourId]);
    }, [storageState.completed]);

    const currentTourDefinition = useMemo(() => {
        return TOUR_DEFINITIONS[activeTourId] || TOUR_DEFINITIONS.taskBoard;
    }, [activeTourId]);

    const value = useMemo(() => ({
        isOpen,
        activeTourId,
        currentStep,
        currentTourDefinition,
        completedTours: storageState.completed || {},
        inviteDismissed: Boolean(storageState.inviteDismissed),
        startTour,
        closeTour,
        completeActiveTour,
        handleStepChange,
        dismissInvite,
        resetProgress,
        isCompleted,
        registerStepChangeListener,
        availableTours: Object.values(TOUR_DEFINITIONS),
    }), [
        isOpen,
        activeTourId,
        currentStep,
        currentTourDefinition,
        storageState.completed,
        storageState.inviteDismissed,
        startTour,
        closeTour,
        completeActiveTour,
        handleStepChange,
        dismissInvite,
        resetProgress,
        isCompleted,
        registerStepChangeListener,
    ]);

    return (
        <ProjectManagementTourContext.Provider value={value}>
            {children}
        </ProjectManagementTourContext.Provider>
    );
}

export function ProjectManagementTourProvider({ children }) {
    const parentContext = useContext(ProjectManagementTourContext);
    if (parentContext) {
        return <>{children}</>;
    }
    return (
        <ProjectManagementTourProviderInternal>
            {children}
        </ProjectManagementTourProviderInternal>
    );
}

export function useProjectTour() {
    const context = useContext(ProjectManagementTourContext);
    if (!context) {
        return {
            isOpen: false,
            activeTourId: null,
            currentStep: 0,
            startTour: () => {},
            closeTour: () => {},
            completeActiveTour: () => {},
            handleStepChange: () => {},
            dismissInvite: () => {},
            resetProgress: () => {},
            isCompleted: () => false,
            availableTours: [],
            completedTours: {},
            inviteDismissed: true,
        };
    }
    return context;
}

export default ProjectManagementTourContext;
